import React, { useState } from 'react';
import { Button } from './ui/button';
import { ShoppingBag, Flame, FileText } from 'lucide-react';
import FullMenuModal from './FullMenuModal';

const Menu = () => {
  const [isFullMenuOpen, setIsFullMenuOpen] = useState(false);
  const dishes = [
    {
      name: 'Doner Wrap + Fries',
      price: '£6.50',
      description: 'Freshly sliced doner meat, crisp salad, signature sauce wrapped in warm flatbread',
      image: 'https://images.unsplash.com/photo-1699728088614-7d1d4277414b?w=600&q=80',
      popular: true
    },
    {
      name: 'Classic Burger Meal',
      price: '£4.50',
      description: 'Juicy grilled burger, fries, drink and chicken wings - unbeatable value',
      image: 'https://images.unsplash.com/photo-1708076158453-ad7346b6d2a0?w=600&q=80',
      popular: true
    },
    {
      name: 'Chicken Wings',
      price: '£5.00',
      description: 'Tender, perfectly seasoned wings with your choice of sauce',
      image: 'https://images.unsplash.com/photo-1690965045391-41243154458c?w=600&q=85',
      popular: false
    },
    {
      name: 'Loaded Fries',
      price: '£4.00',
      description: 'Crispy fries topped with cheese, jalapeños and our special sauce',
      image: 'https://images.unsplash.com/photo-1762284513031-3d7ad15562bc?w=600&q=85',
      popular: false
    },
    {
      name: 'Beef Shawarma Wrap',
      price: '£6.00',
      description: 'Marinated beef, fresh vegetables, tahini sauce in warm pita',
      image: 'https://images.unsplash.com/photo-1529006557810-274b9b2fc783?w=600&q=80',
      popular: true
    },
    {
      name: 'Spicy Wings Combo',
      price: '£7.00',
      description: 'Hot wings with signature chili sauce, fries and cooling dip',
      image: 'https://images.unsplash.com/photo-1567620832903-9fc6debc209f?w=600&q=80',
      popular: false
    }
  ];

  const handleOrder = () => {
    window.location.href = 'tel:02078333399';
  };

  return (
    <section id="menu" className="py-20 bg-black relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
        }} />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-orange-600/20 border border-orange-500/50 rounded-full px-6 py-2 mb-6">
            <Flame className="h-5 w-5 text-orange-500" />
            <span className="text-orange-500 font-semibold">Signature Dishes</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Fresh Off The <span className="text-orange-500">Grill</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Every dish grilled to perfection, served fast with generous portions
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {dishes.map((dish, index) => (
            <div 
              key={index}
              className="group relative bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800 hover:border-orange-500 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/20"
            >
              {dish.popular && (
                <div className="absolute top-4 right-4 z-10 bg-gradient-to-r from-orange-500 to-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  POPULAR
                </div>
              )}
              
              <div className="relative h-56 overflow-hidden">
                <img 
                  src={dish.image} 
                  alt={dish.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/20 to-transparent" />
              </div>
              
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-bold text-white group-hover:text-orange-500 transition-colors duration-300">
                    {dish.name}
                  </h3>
                  <span className="text-2xl font-bold text-orange-500">{dish.price}</span>
                </div>
                
                <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                  {dish.description}
                </p>
                
                <Button 
                  className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/50"
                  onClick={handleOrder}
                >
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  Order Now
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-gray-400 mb-6">Want to see our full menu?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-orange-500/50"
              onClick={() => setIsFullMenuOpen(true)}
            >
              <FileText className="mr-2 h-5 w-5" />
              View Complete Menu
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white px-8 py-6 text-lg font-semibold transition-all duration-300"
              onClick={handleOrder}
            >
              Call for Orders
            </Button>
          </div>
        </div>
      </div>
      
      <FullMenuModal isOpen={isFullMenuOpen} onClose={() => setIsFullMenuOpen(false)} />
    </section>
  );
};

export default Menu;
